/**
 * Free shipping amounts in the customer's currency.
 *
 * The threshold is set in the shop's currency (kr) in the cart drawer section, per country, like the free shipping
 * rules in Shopify. cart-drawer.liquid picks the rule for the customer's country and writes it to #FreeShippingData.
 * In another currency it is converted here with Shopify's exchange rate, the one checkout uses, and rounded up to a
 * whole amount, so free shipping is never promised too early. Liquid has no exchange rate, which is why this is a
 * script.
 *
 * - <free-shipping-bar> is the bar in the cart drawer. In the shop's currency the server renders it, and this only
 *   takes over in other currencies.
 * - <free-shipping-threshold> is the threshold as text, e.g. "[threshold]" in the announcement bar.
 */

/**
 * @typedef {object} FreeShippingData
 * @property {number} threshold - In the shop's currency, minor units
 * @property {string} currency - The cart's currency
 * @property {string} shopCurrency
 * @property {string} moneyFormat - 1234.56 formatted by the money filter in the cart's currency
 * @property {string} moneyFormatWhole - 1234 formatted by money_without_trailing_zeros in the cart's currency
 */

/** @type {FreeShippingData | null} */
const data = readData();

/** @returns {FreeShippingData | null} */
function readData() {
  const element = document.getElementById('FreeShippingData');
  if (!element?.textContent) return null;

  try {
    return JSON.parse(element.textContent);
  } catch {
    return null;
  }
}

/**
 * The threshold in the cart's currency, in minor units, or null when it can't be converted.
 * @returns {number | null}
 */
function getThreshold() {
  if (!data) return null;
  if (data.currency === data.shopCurrency) return data.threshold;

  // @ts-ignore Shopify's global, set on every storefront page
  const { active, rate } = window.Shopify?.currency ?? {};
  if (active !== data.currency || !Number(rate)) return null;

  return Math.ceil((data.threshold / 100) * Number(rate)) * 100;
}

/**
 * Formats an amount like the money filter does in the cart's currency. The symbol, the separators and their places
 * differ per currency, so they are read from the samples the server formatted.
 * @param {number} minorUnits
 * @returns {string | null}
 */
function formatMoney(minorUnits) {
  if (!data) return null;

  const whole = minorUnits % 100 === 0;
  const sample = decodeEntities(whole ? data.moneyFormatWhole : data.moneyFormat);
  const match = whole ? sample.match(/^(.*?)1(\D?)234(.*)$/s) : sample.match(/^(.*?)1(\D?)234(\D)56(.*)$/s);

  // A currency shown without decimals (like kr): round to a whole amount
  if (!match) return whole ? null : formatMoney(Math.round(minorUnits / 100) * 100);

  const [, prefix = '', thousands = '', ...rest] = match;
  const amount = Math.trunc(minorUnits / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

  if (whole) return `${prefix}${amount}${rest[0]}`;

  const [decimal, suffix] = rest;
  const cents = String(minorUnits % 100).padStart(2, '0');

  return `${prefix}${amount}${decimal}${cents}${suffix}`;
}

/**
 * @param {string} text
 * @returns {string}
 */
function decodeEntities(text) {
  return new DOMParser().parseFromString(text, 'text/html').documentElement.textContent ?? '';
}

class FreeShippingBar extends HTMLElement {
  /** The drawer is morphed after cart changes, which puts the server's empty text and the new total back */
  #observer = new MutationObserver(() => this.#render());

  connectedCallback() {
    this.#render();
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-total'],
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  /** Only writes what differs, as every write would wake the observer again */
  #render() {
    if (!data || data.currency === data.shopCurrency) return;

    const threshold = getThreshold();
    const total = Number(this.dataset.total);
    const text = this.querySelector('.cart-drawer__shipping-bar-text');
    const progress = /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer__shipping-bar-progress'));
    const remaining = threshold === null ? null : threshold - total;
    const amount = remaining === null || remaining <= 0 ? null : formatMoney(remaining);

    // Without an exchange rate, or an amount that can be formatted, no bar is better than a wrong one
    if (threshold === null || Number.isNaN(total) || !text || !progress || (remaining > 0 && !amount)) {
      this.hidden = true;
      return;
    }

    const remainingLabel = this.dataset.remainingLabel ?? '';
    const unlockedLabel = this.dataset.unlockedLabel ?? '';
    const expected = amount ? `${amount} ${remainingLabel}` : unlockedLabel;

    if (text.textContent !== expected) {
      if (amount) {
        const amountElement = document.createElement('span');
        amountElement.className = 'cart-drawer__shipping-bar-amount';
        amountElement.textContent = amount;
        text.replaceChildren(amountElement, ` ${remainingLabel}`);
      } else {
        text.textContent = unlockedLabel;
      }
    }

    const width = `${Math.min(100, Math.floor((total / threshold) * 100))}%`;
    if (progress.style.width !== width) progress.style.width = width;
  }
}

class FreeShippingThreshold extends HTMLElement {
  connectedCallback() {
    const threshold = getThreshold();
    const text = (threshold !== null && formatMoney(threshold)) || '';

    if (this.textContent !== text) this.textContent = text;
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}

if (!customElements.get('free-shipping-threshold')) {
  customElements.define('free-shipping-threshold', FreeShippingThreshold);
}
